// src/services/db.js
// Architecture multi-tenant V2
// Toutes les données sont isolées par organisation : /organisations/{orgId}/...

import {
  collection, doc, getDoc, getDocs, setDoc, updateDoc, deleteDoc,
  addDoc, onSnapshot, query, orderBy, where, writeBatch, increment,
  serverTimestamp, runTransaction,
} from "firebase/firestore";
import { db } from "./firebase";
import { getConfirmation, lancerTirageServeur } from "./brevo";

// ─── HELPERS CHEMINS ─────────────────────────────────────────────────────────
export const orgRef = (orgId) => doc(db, "organisations", orgId);
export const tombolasRef = (orgId) => collection(db, "organisations", orgId, "tombolas");
export const tombolaRef = (orgId, tombolaId) => doc(db, "organisations", orgId, "tombolas", tombolaId);
export const billetsRef = (orgId, tombolaId) => collection(db, "organisations", orgId, "tombolas", tombolaId, "billets");
export const lotsRef = (orgId, tombolaId) => collection(db, "organisations", orgId, "tombolas", tombolaId, "lots");
export const metaRef = (orgId, tombolaId, docId) => doc(db, "organisations", orgId, "tombolas", tombolaId, "meta", docId);
export const statsRef = (orgId, tombolaId) => doc(db, "organisations", orgId, "tombolas", tombolaId, "statistiques", "global");
export const userRef = (userId) => doc(db, "users", userId);
export const membresRef = (orgId) => collection(db, "organisations", orgId, "membres");
export const membreRef = (orgId, userId) => doc(db, "organisations", orgId, "membres", userId);
// Membres par tombola (nouveau modèle)
export const membresTombolaRef = (orgId, tombolaId) => collection(db, "organisations", orgId, "tombolas", tombolaId, "membres");
export const membreTombolaRef = (orgId, tombolaId, userId) => doc(db, "organisations", orgId, "tombolas", tombolaId, "membres", userId);
export const invitationsTombolaRef = (orgId, tombolaId) => collection(db, "organisations", orgId, "tombolas", tombolaId, "invitations");
export const invitationTombolaRef = (orgId, tombolaId, invitId) => doc(db, "organisations", orgId, "tombolas", tombolaId, "invitations", invitId);

// ─── ORGANISATIONS ────────────────────────────────────────────────────────────
export async function creerOrganisation(orgId, { nom, plan = "freemium", adminEmail, adminId, codePromo = null }) {
  // Prénom saisi par l'utilisateur (profil), sinon début de l'email
  let pseudoAdmin = adminEmail.split("@")[0];
  try {
    const profilSnap = await getDoc(userRef(adminId));
    pseudoAdmin = profilSnap.data()?.pseudo || pseudoAdmin;
  } catch { /* profil illisible : on garde le début de l'email */ }

  const batch = writeBatch(db);

  // Créer l'organisation
  batch.set(orgRef(orgId), {
    nom: nom.trim(),
    plan,
    adminId,
    adminEmail,
    createdAt: serverTimestamp(),
    stripeCustomerId: null,
    codePromo,
  });

  // Créer le membre admin dans l'organisation
  batch.set(membreRef(orgId, adminId), {
    userId: adminId,
    email: adminEmail,
    role: "admin",
    pseudo: pseudoAdmin,
    joinedAt: serverTimestamp(),
  });

  await batch.commit();

  // Ajouter l'orgId dans le profil utilisateur (après le batch)
  const userSnap = await getDoc(userRef(adminId));
  const existingOrgIds = userSnap.exists() ? (userSnap.data().orgIds || []) : [];
  if (!existingOrgIds.includes(orgId)) {
    await setDoc(userRef(adminId), {
      orgIds: [...existingOrgIds, orgId],
    }, { merge: true });
  }
}

// Récupérer toutes les organisations d'un utilisateur
// Utilise la liste orgIds stockée dans le profil utilisateur
export async function getOrgsDeUser(userId) {
  const userSnap = await getDoc(userRef(userId));
  if (!userSnap.exists()) return [];

  const orgIds = userSnap.data().orgIds || [];
  if (orgIds.length === 0) return [];

  const orgs = [];
  for (const orgId of orgIds) {
    try {
      const [orgSnap, membreSnap] = await Promise.all([
        getDoc(orgRef(orgId)),
        getDoc(membreRef(orgId, userId)),
      ]);
      if (orgSnap.exists() && membreSnap.exists()) {
        orgs.push({
          org: { id: orgSnap.id, ...orgSnap.data() },
          role: membreSnap.data().role,
          pseudo: membreSnap.data().pseudo,
        });
      }
    } catch { /* org inaccessible, on skip */ }
  }
  return orgs;
}

// Récupérer le rôle d'un user dans une org
export async function getRoleInOrg(orgId, userId) {
  const snap = await getDoc(membreRef(orgId, userId));
  return snap.exists() ? snap.data().role : null;
}

export async function getOrganisation(orgId) {
  const snap = await getDoc(orgRef(orgId));
  return snap.exists() ? { id: snap.id, ...snap.data() } : null;
}

export function ecouterOrganisation(orgId, callback) {
  return onSnapshot(orgRef(orgId), (snap) => {
    callback(snap.exists() ? { id: snap.id, ...snap.data() } : null);
  });
}

// ─── UTILISATEURS ─────────────────────────────────────────────────────────────
export async function creerProfil(userId, { email, pseudo = "" }) {
  await setDoc(userRef(userId), {
    email,
    pseudo: pseudo || email.split("@")[0],
    createdAt: serverTimestamp(),
    orgIds: [],
  });
}

export async function getProfil(userId) {
  const snap = await getDoc(userRef(userId));
  return snap.exists() ? { id: snap.id, ...snap.data() } : null;
}

export function ecouterProfil(userId, callback) {
  return onSnapshot(userRef(userId), (snap) => {
    callback(snap.exists() ? { id: snap.id, ...snap.data() } : null);
  });
}

export async function updatePseudo(userId, pseudo) {
  await updateDoc(userRef(userId), { pseudo: pseudo.trim() });
}

// ─── MEMBRES ──────────────────────────────────────────────────────────────────
export function ecouterMembres(orgId, callback) {
  return onSnapshot(membresRef(orgId), (snap) => {
    callback(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
  });
}

// ─── MEMBRES PAR TOMBOLA (nouveau modèle) ────────────────────────────────────
export function ecouterMembresTombola(orgId, tombolaId, callback) {
  return onSnapshot(membresTombolaRef(orgId, tombolaId), (snap) => {
    callback(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
  });
}

// Invitations : création et acceptation via Cloud Functions
// (voir creerInvitations / accepterInvitation dans services/brevo.js)

export async function getRoleDansTombola(orgId, tombolaId, userId) {
  const snap = await getDoc(membreTombolaRef(orgId, tombolaId, userId));
  return snap.exists() ? snap.data().role : null;
}

export async function changerRoleMembreTombola(orgId, tombolaId, userId, role) {
  await updateDoc(membreTombolaRef(orgId, tombolaId, userId), { role });
}

export async function supprimerMembreTombola(orgId, tombolaId, userId) {
  await deleteDoc(membreTombolaRef(orgId, tombolaId, userId));
}

// ─── TOMBOLAS ─────────────────────────────────────────────────────────────────
export async function creerTombola(orgId, { nom, annee }, adminId, adminEmail) {
  // Si adminId non fourni, le récupérer depuis l'org
  if (!adminId || !adminEmail) {
    const orgSnap = await getDoc(orgRef(orgId));
    if (!orgSnap.exists()) throw new Error("Organisation introuvable.");
    adminId = orgSnap.data().adminId;
    adminEmail = orgSnap.data().adminEmail;
  }

  // Prénom saisi par l'utilisateur (profil), sinon début de l'email
  let pseudoAdmin = adminEmail.split("@")[0];
  try {
    const profilSnap = await getDoc(userRef(adminId));
    pseudoAdmin = profilSnap.data()?.pseudo || pseudoAdmin;
  } catch { /* profil illisible : on garde le début de l'email */ }

  // Générer un ID pour la tombola
  const tombolaDocRef = doc(tombolasRef(orgId));
  const tombolaId = tombolaDocRef.id;

  // Tout dans un seul batch : tombola + membre admin + meta
  const batch = writeBatch(db);

  // 1. Créer la tombola
  batch.set(tombolaDocRef, {
    nom: nom.trim(),
    annee: annee || new Date().getFullYear(),
    archived: false,
    plan: "starter",
    stripeSessionId: null,
    paidAt: null,
    createdAt: serverTimestamp(),
  });

  // 2. Créer le membre admin de la tombola
  batch.set(doc(db, "organisations", orgId, "tombolas", tombolaId, "membres", adminId), {
    userId: adminId,
    email: adminEmail,
    role: "admin",
    pseudo: pseudoAdmin,
    joinedAt: serverTimestamp(),
  });

  // 3. Initialiser les paramètres par défaut
  batch.set(metaRef(orgId, tombolaId, "parametres"), {
    prixTicket: 1,
    methodesEncaissement: ["Espèces", "Carte Bancaire", "Chèque"],
  });
  batch.set(metaRef(orgId, tombolaId, "statut"), {
    ventesClosees: false,
  });

  await batch.commit();

  return tombolaId;
}

export function ecouterTombolas(orgId, callback, onError) {
  return onSnapshot(
    query(tombolasRef(orgId), orderBy("createdAt", "desc")),
    (snap) => callback(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
    (err) => { console.warn("Lecture des tombolas impossible :", err?.code); if (onError) onError(err); }
  );
}

export async function archiverTombola(orgId, tombolaId) {
  await updateDoc(tombolaRef(orgId, tombolaId), { archived: true });
}

export async function supprimerTombola(orgId, tombolaId) {
  await deleteDoc(tombolaRef(orgId, tombolaId));
}

// Le passage en Pro est fait UNIQUEMENT par le webhook Stripe (Cloud Function)

// ─── PARAMÈTRES ───────────────────────────────────────────────────────────────
export function ecouterParametres(orgId, tombolaId, callback) {
  return onSnapshot(metaRef(orgId, tombolaId, "parametres"), (snap) => {
    callback(snap.exists() ? snap.data() : {
      prixTicket: 1,
      methodesEncaissement: ["Espèces", "Carte Bancaire", "Chèque"],
    });
  });
}

export async function saveParametres(orgId, tombolaId, data) {
  await setDoc(metaRef(orgId, tombolaId, "parametres"), data, { merge: true });
}

// ─── STATUT VENTES ────────────────────────────────────────────────────────────
export function ecouterStatut(orgId, tombolaId, callback) {
  return onSnapshot(metaRef(orgId, tombolaId, "statut"), (snap) => {
    callback(snap.exists() ? snap.data() : { ventesClosees: false });
  });
}

export async function cloturerVentes(orgId, tombolaId) {
  await setDoc(metaRef(orgId, tombolaId, "statut"), { ventesClosees: true }, { merge: true });
}

export async function reouvrir(orgId, tombolaId) {
  await setDoc(metaRef(orgId, tombolaId, "statut"), { ventesClosees: false }, { merge: true });
}

// ─── PRÉFIXE APPAREIL (hors-ligne) ───────────────────────────────────────────
const LETTRES = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";

function getPrefixeLocal(orgId, tombolaId) {
  return localStorage.getItem(`tl_prefixe_${orgId}_${tombolaId}`);
}

function getCompteurLocal(orgId, tombolaId) {
  return parseInt(localStorage.getItem(`tl_compteur_${orgId}_${tombolaId}`) || "0");
}

function setCompteurLocal(orgId, tombolaId, val) {
  localStorage.setItem(`tl_compteur_${orgId}_${tombolaId}`, String(val));
}

export function resetPrefixeLocal(orgId, tombolaId) {
  localStorage.removeItem(`tl_prefixe_${orgId}_${tombolaId}`);
  localStorage.removeItem(`tl_compteur_${orgId}_${tombolaId}`);
}

// Verrou en mémoire pour éviter les doubles appels (React StrictMode)
const prefixeLocks = {};

export async function initialiserPrefixe(orgId, tombolaId) {
  const existing = getPrefixeLocal(orgId, tombolaId);
  if (existing) return existing;

  // Éviter les appels simultanés
  const lockKey = `${orgId}_${tombolaId}`;
  if (prefixeLocks[lockKey]) return prefixeLocks[lockKey];

  const prefixeRef = metaRef(orgId, tombolaId, "prefixeCounter");
  
  const promise = (async () => {
    try {
      let lettre;
      await runTransaction(db, async (transaction) => {
        const snap = await transaction.get(prefixeRef);
        const index = snap.exists() ? snap.data().index : 0;
        lettre = index < LETTRES.length
          ? LETTRES[index]
          : `${LETTRES[Math.floor(index / 26) - 1]}${LETTRES[index % 26]}`;
        transaction.set(prefixeRef, { index: index + 1 });
      });
      localStorage.setItem(`tl_prefixe_${orgId}_${tombolaId}`, lettre);
      return lettre;
    } catch (e) {
      if (e.code === "already-exists" || e.code === "aborted") {
        // Transaction conflict - lire la valeur existante
        const snap = await getDoc(prefixeRef);
        const index = snap.exists() ? snap.data().index - 1 : 0;
        const lettre = index < LETTRES.length
          ? LETTRES[index]
          : `${LETTRES[Math.floor(index / 26) - 1]}${LETTRES[index % 26]}`;
        localStorage.setItem(`tl_prefixe_${orgId}_${tombolaId}`, lettre);
        return lettre;
      }
      throw e;
    } finally {
      delete prefixeLocks[lockKey];
    }
  })();

  prefixeLocks[lockKey] = promise;
  return promise;
}

// ─── BILLETS ──────────────────────────────────────────────────────────────────
export async function ajouterBillets(orgId, tombolaId, { nom, telephone, email, quantite, methodeEncaissement }, userEmail, prefixe) {
  const qte = Number(quantite);
  const startNum = getCompteurLocal(orgId, tombolaId) + 1;
  setCompteurLocal(orgId, tombolaId, startNum + qte - 1);

  const venteId = `${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
  const venteAt = new Date().toISOString();
  const batch = writeBatch(db);

  const numeros = [];
  for (let i = 0; i < qte; i++) {
    const numero = `${prefixe}-${startNum + i}`;
    numeros.push(numero);
    const billetRef = doc(billetsRef(orgId, tombolaId));
    batch.set(billetRef, {
      numero,
      nom: nom.trim(),
      telephone: telephone?.trim() || "",
      email: email?.trim() || "",
      methodeEncaissement: methodeEncaissement || "Espèces",
      saisiPar: userEmail || "inconnu",
      venteId,
      venteAt,
      quantiteVente: qte,
      createdAt: serverTimestamp(),
      gagnant: false,
    });
  }

  // Incrémenter stats agrégées
  batch.set(statsRef(orgId, tombolaId), {
    totalBillets: increment(qte),
    totalVentes: increment(1),
  }, { merge: true });

  await batch.commit();
  return { numeros, venteId };
}

export async function getBilletsOnce(orgId, tombolaId) {
  const snap = await getDocs(billetsRef(orgId, tombolaId));
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

export function ecouterStatistiques(orgId, tombolaId, callback) {
  return onSnapshot(statsRef(orgId, tombolaId), (snap) => {
    callback(snap.exists() ? snap.data() : { totalBillets: 0, totalVentes: 0 });
  });
}

// ─── LOTS ─────────────────────────────────────────────────────────────────────
export function ecouterLots(orgId, tombolaId, callback) {
  return onSnapshot(
    query(lotsRef(orgId, tombolaId), orderBy("rang", "asc")),
    (snap) => callback(snap.docs.map((d) => ({ id: d.id, ...d.data() })))
  );
}

export async function ajouterLot(orgId, tombolaId, { description, valeur, rang, offertPar }) {
  await addDoc(lotsRef(orgId, tombolaId), {
    description: description.trim(),
    valeur: valeur?.trim() || "",
    offertPar: offertPar?.trim() || "",
    rang: Number(rang) || 1,
    createdAt: serverTimestamp(),
    gagnantBilletId: null,
  });
}

export async function modifierLot(orgId, tombolaId, lotId, data) {
  await updateDoc(doc(lotsRef(orgId, tombolaId), lotId), {
    description: data.description.trim(),
    valeur: data.valeur?.trim() || "",
    offertPar: data.offertPar?.trim() || "",
    rang: Number(data.rang) || 1,
  });
}

export async function supprimerLot(orgId, tombolaId, lotId) {
  await deleteDoc(doc(lotsRef(orgId, tombolaId), lotId));
}

export async function reordonnerLots(orgId, tombolaId, lotsOrdonnes) {
  const updates = lotsOrdonnes.map((lot, index) =>
    updateDoc(doc(lotsRef(orgId, tombolaId), lot.id), { rang: index + 1 })
  );
  await Promise.all(updates);
}

// ─── TIRAGE ───────────────────────────────────────────────────────────────────
export function secureRandomInt(max) {
  const array = new Uint32Array(1);
  let result;
  do {
    crypto.getRandomValues(array);
    result = array[0];
  } while (result >= Math.floor(0x100000000 / max) * max);
  return result % max;
}

// Tirage effectué côté serveur (Cloud Function lancerTirage) :
// journalisé, vérifiable, impossible à rejouer depuis l'app.
// Clôture automatiquement les ventes.
export async function lancerTirage(orgId, tombolaId) {
  const res = await lancerTirageServeur({ orgId, tombolaId });
  return res.resultats || [];
}

// ─── CONFIRMATION PUBLIQUE ────────────────────────────────────────────────────
// Servie par la Cloud Function getConfirmation : seule CETTE vente est renvoyée,
// téléphone masqué. Les billets ne sont plus lisibles publiquement.
export async function getVentePublique(venteId, orgId, tombolaId) {
  if (!venteId || !orgId || !tombolaId) return null;
  try {
    return await getConfirmation({ venteId, orgId, tombolaId });
  } catch {
    return null;
  }
}

export async function getLotsPublics(orgId, tombolaId) {
  const snap = await getDocs(query(lotsRef(orgId, tombolaId), orderBy("rang", "asc")));
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

// ─── MODIFIER / SUPPRIMER UNE VENTE ──────────────────────────────────────────
export async function modifierVente(orgId, tombolaId, venteId, formData, modifiePar) {
  const snap = await getDocs(query(billetsRef(orgId, tombolaId), where("venteId", "==", venteId)));
  const batch = writeBatch(db);
  snap.docs
    .forEach((d) => {
      batch.update(d.ref, {
        nom: formData.nom.trim(),
        telephone: formData.telephone?.trim() || "",
        email: formData.email?.trim() || "",
        methodeEncaissement: formData.methodeEncaissement,
        modifiePar: modifiePar || "",
        modifieAt: new Date().toISOString(),
      });
    });
  await batch.commit();
}

export async function supprimerVente(orgId, tombolaId, venteId) {
  const snap = await getDocs(query(billetsRef(orgId, tombolaId), where("venteId", "==", venteId)));
  if (snap.empty) return;
  if (snap.docs.some((d) => d.data().gagnant === true)) {
    throw new Error("Impossible de supprimer une vente contenant un billet gagnant.");
  }
  const batch = writeBatch(db);
  const qte = snap.size;
  snap.docs.forEach((d) => batch.delete(d.ref));
  // Décrémenter stats
  batch.set(statsRef(orgId, tombolaId), {
    totalBillets: increment(-qte),
    totalVentes: increment(-1),
  }, { merge: true });
  await batch.commit();
}

// ─── LECTURES UNIQUES ────────────────────────────────────────────────────────
export async function getParametresOnce(orgId, tombolaId) {
  const snap = await getDoc(metaRef(orgId, tombolaId, "parametres"));
  return snap.exists() ? snap.data() : {
    prixTicket: 1,
    methodesEncaissement: ["Espèces", "Carte Bancaire", "Chèque"],
  };
}

export async function getStatutOnce(orgId, tombolaId) {
  const snap = await getDoc(metaRef(orgId, tombolaId, "statut"));
  return snap.exists() ? snap.data() : { ventesClosees: false };
}
